import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { BookOpen, Pencil, Trash, Plus, X, Save } from 'lucide-react';

const TemplateManagerDialog = ({
  showTemplateManager,
  setShowTemplateManager,
  customTemplates,
  editingTemplate,
  templateForm,
  setTemplateForm,
  resetTemplateForm,
  openEditTemplate,
  deleteTemplate,
  saveTemplate,
  translate,
}) => {
  return (
    <Dialog open={showTemplateManager} onOpenChange={(open) => { if (!open) { resetTemplateForm(); } setShowTemplateManager(open); }}>
      <DialogContent className="bg-gradient-to-br from-blue-900 to-purple-900 border-purple-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="template-manager-dialog">
        <DialogHeader>
          <DialogTitle className="text-purple-200 flex items-center">
            <BookOpen className="mr-2" size={20} />
            {editingTemplate ? translate('editTemplate', 'Edit Template') : translate('createTemplate', 'Create Custom Template')}
          </DialogTitle>
        </DialogHeader>

        {/* List existing custom templates */}
        {!editingTemplate && customTemplates.length > 0 && (
          <div className="mb-4 space-y-2">
            <h4 className="text-sm font-semibold text-purple-300">{translate('yourTemplates', 'Your Custom Templates')}</h4>
            {customTemplates.map(ct => (
              <div key={ct.id} className="flex items-center justify-between p-3 bg-purple-800/40 rounded-lg border border-purple-600/30">
                <div>
                  <p className="text-purple-100 font-medium">{ct.name}</p>
                  <p className="text-purple-300 text-xs">{ct.discipline} - {ct.duration_minutes} min{ct.description ? ` - ${ct.description}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-purple-300 hover:text-white hover:bg-purple-700/50" onClick={() => openEditTemplate(ct)} data-testid={`edit-template-${ct.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-100 hover:bg-red-700/50" onClick={() => deleteTemplate(ct.id)} data-testid={`delete-template-${ct.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
            <hr className="border-purple-700/50 my-3" />
            <h4 className="text-sm font-semibold text-purple-300">{editingTemplate ? translate('editTemplate', 'Edit Template') : translate('newTemplate', 'New Template')}</h4>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-purple-200 text-sm">{translate('templateName', 'Template Name')}</label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g., Advanced DYN Training" className="bg-purple-800/50 border-purple-600 text-white mt-1" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-purple-200 text-sm">{translate('discipline', 'Discipline')}</label>
              <Select value={templateForm.discipline} onValueChange={(v) => setTemplateForm({ ...templateForm, discipline: v })}>
                <SelectTrigger className="bg-purple-800/50 border-purple-600 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="STA">STA</SelectItem>
                  <SelectItem value="DYN">DYN</SelectItem>
                  <SelectItem value="DYNBF">DYNBF</SelectItem>
                  <SelectItem value="MIX">MIX</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-purple-200 text-sm">{translate('duration', 'Duration')} (min)</label>
              <Input type="number" value={templateForm.duration_minutes} onChange={(e) => setTemplateForm({ ...templateForm, duration_minutes: parseInt(e.target.value) || 0 })} className="bg-purple-800/50 border-purple-600 text-white mt-1" />
            </div>
            <div>
              <label className="text-purple-200 text-sm">{translate('totalDistance', 'Total Distance')} (m)</label>
              <Input type="number" value={templateForm.total_distance} onChange={(e) => setTemplateForm({ ...templateForm, total_distance: parseInt(e.target.value) || 0 })} className="bg-purple-800/50 border-purple-600 text-white mt-1" />
            </div>
          </div>
          <div>
            <label className="text-purple-200 text-sm">{translate('description', 'Description')}</label>
            <Textarea value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} className="bg-purple-800/50 border-purple-600 text-white mt-1" rows={2} />
          </div>

          {/* Template Blocks */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-purple-200 text-sm font-semibold">{translate('blocks', 'Blocks')}</label>
              <Button size="sm" variant="outline" className="border-purple-500 text-purple-200" onClick={() => setTemplateForm({ ...templateForm, blocks: [...templateForm.blocks, { name: `Block ${templateForm.blocks.length + 1}`, duration_minutes: 15, exercises: [] }] })}>
                <Plus size={12} className="mr-1" /> {translate('addBlock', 'Add Block')}
              </Button>
            </div>
            {templateForm.blocks.map((block, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-purple-800/30 rounded">
                <Input value={block.name} onChange={(e) => { const blocks = [...templateForm.blocks]; blocks[idx] = { ...blocks[idx], name: e.target.value }; setTemplateForm({ ...templateForm, blocks }); }} className="bg-purple-700/50 border-purple-600 text-white text-sm flex-1" />
                <Input type="number" value={block.duration_minutes} onChange={(e) => { const blocks = [...templateForm.blocks]; blocks[idx] = { ...blocks[idx], duration_minutes: parseInt(e.target.value) || 0 }; setTemplateForm({ ...templateForm, blocks }); }} className="bg-purple-700/50 border-purple-600 text-white text-sm w-20" />
                <span className="text-purple-300 text-xs">min</span>
                <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-700/50 h-7 w-7 p-0" onClick={() => { const blocks = templateForm.blocks.filter((_, i) => i !== idx); setTemplateForm({ ...templateForm, blocks }); }}>
                  <X size={12} />
                </Button>
              </div>
            ))}
          </div>
          <div>
            <label className="text-purple-200 text-sm">{translate('notes', 'Notes')}</label>
            <Textarea value={templateForm.notes} onChange={(e) => setTemplateForm({ ...templateForm, notes: e.target.value })} className="bg-purple-800/50 border-purple-600 text-white mt-1" rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-purple-600 text-purple-200" onClick={() => { resetTemplateForm(); setShowTemplateManager(false); }}>{translate('cancel', 'Cancel')}</Button>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={saveTemplate} disabled={!templateForm.name} data-testid="save-template-btn">
            <Save className="mr-2" size={14} />
            {editingTemplate ? translate('updateTemplate', 'Update Template') : translate('saveTemplate', 'Save Template')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateManagerDialog;
